<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <div>

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-user" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-1 font-weight-bold">{{ dependent.full_name || 'Dependent Profile' }}</h4>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <span v-if="dependent.status == 'active'" class="header-status header-status--active">
                  <i class="fe fe-check-circle mr-1"></i> Active
                </span>
                <span v-else class="header-status header-status--inactive">
                  <i class="fe fe-alert-circle mr-1"></i> Inactive
                </span>
                <span class="header-id-badge" v-if="dependent.id_card_number">
                  {{ dependent.id_card_number }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Extra Dependent Renewal -->
      <div class="container mt-n3 pb-0" v-if="needsRenewal">
        <div class="card shadow table-card mb-4">
          <div class="card-body py-3 px-4 d-flex align-items-center flex-wrap" style="gap: 12px;">
            <i class="fe fe-refresh-cw text-primary" style="font-size: 1.3rem;"></i>
            <div class="mr-auto">
              <p class="mb-0 font-weight-bold">
                {{ dependent.status === 'active' ? 'Renew Subscription' : 'This Extra Dependent is inactive' }}
              </p>
              <p class="mb-0 text-muted" style="font-size: .85rem;">
                Pay &#8358;{{ renewalAmount }} to extend coverage 12 months from today{{ dependent.status === 'active' ? '' : ' and reactivate this dependent' }}.
              </p>
            </div>
            <paystack :amount="renewalAmount * 100" :email="user.email" :subaccount="agency.virtual_account"
              :paystackkey="paystackkey" :reference="renewalReference" :callback="onRenewalPaymentSuccess"
              :close="onRenewalPaymentClose" :embed="false">
              <button type="button" class="btn btn-submit" :disabled="isLoading">
                <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Processing…</span>
                <span v-else><i class="fe fe-credit-card mr-1"></i> Pay &#8358;{{ renewalAmount }} &amp; Renew</span>
              </button>
            </paystack>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div class="row">
          <div class="col-lg-12">

            <!-- Profile Card -->
            <div class="card table-card shadow mb-4">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-user" style="font-size:13px;"></i></div>
                <div>
                  <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Dependent Profile</p>
                  <small style="color:rgba(255,255,255,.7);">Personal and coverage details</small>
                </div>
              </div>
              <div class="card-body row">

                <!-- Photo Column -->
                <div class="col-md-3 text-center border-right pt-3 pb-3">
                  <vue-initials-img
                    :name="dependent.firstname + ' ' + dependent.lastname"
                    class="rounded-circle mx-auto mb-3"
                    style="width:100px;height:100px;"
                    v-if="dependent.image == null"
                  />
                  <div v-if="dependent.image != null" class="mb-3">
                    <img
                      :src="`${dependent.image_url}`"
                      class="rounded-circle mx-auto d-block"
                      style="width:100px;height:100px;object-fit:cover;"
                    />
                  </div>
                  <p class="font-weight-bold mb-0">{{ dependent.firstname }} {{ dependent.lastname }}</p>
                  <small class="text-muted">Dependent</small>
                  <div class="mt-2" v-if="dependent.status == 'active'">
                    <span class="badge badge-soft-success px-3 py-1" style="border-radius:20px;font-size:.75rem;">
                      <i class="fe fe-check-circle mr-1"></i> Active
                    </span>
                  </div>
                  <div class="mt-2" v-else>
                    <span class="badge badge-soft-warning px-3 py-1" style="border-radius:20px;font-size:.75rem;">
                      <i class="fe fe-alert-circle mr-1"></i> Inactive
                    </span>
                  </div>
                </div>

                <!-- Info Column -->
                <div class="col-md-9 p-0">
                  <ul class="list-unstyled mb-0">
                    <li class="info-row">
                      <i class="fe fe-user info-icon"></i>
                      <div>
                        <p class="info-label">Name</p>
                        <p class="info-value">{{ dependent.firstname }} {{ dependent.lastname }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-credit-card info-icon"></i>
                      <div>
                        <p class="info-label">Enrollee ID</p>
                        <p class="info-value"><span class="code-chip">{{ dependent.id_card_number }}</span></p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-users info-icon"></i>
                      <div>
                        <p class="info-label">Principal</p>
                        <p class="info-value">
                          {{ dependent.user && dependent.user.full_name }}
                          <span class="code-chip ml-1" v-if="dependent.user">{{ dependent.user.id_card_number }}</span>
                        </p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-phone info-icon"></i>
                      <div>
                        <p class="info-label">Phone Number</p>
                        <p class="info-value">{{ dependent.phone_number }}</p>
                      </div>
                    </li>
                    <li class="info-row" v-if="dependent.localgovt">
                      <i class="fe fe-map-pin info-icon"></i>
                      <div>
                        <p class="info-label">LGA</p>
                        <p class="info-value">{{ dependent.localgovt.local_name }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-gift info-icon"></i>
                      <div>
                        <p class="info-label">Date of Birth</p>
                        <p class="info-value">{{ dependent.dob | moment("dddd, MMMM Do YYYY") }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-user info-icon"></i>
                      <div>
                        <p class="info-label">Gender</p>
                        <p class="info-value">{{ dependent.gender }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-link info-icon"></i>
                      <div>
                        <p class="info-label">Relationship Type</p>
                        <p class="info-value">{{ dependent.relationShipType }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-hash info-icon"></i>
                      <div>
                        <p class="info-label">NIN</p>
                        <p class="info-value">{{ dependent.nimc_number }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-calendar info-icon"></i>
                      <div>
                        <p class="info-label">Enrollment Date</p>
                        <p class="info-value">{{ dependent.created_at | moment("dddd, MMMM Do YYYY") }}</p>
                      </div>
                    </li>
                    <li class="info-row">
                      <i class="fe fe-calendar info-icon"></i>
                      <div>
                        <p class="info-label">Expiry Date</p>
                        <p class="info-value">
                          {{ dependent.expiry_date | moment("dddd, MMMM Do YYYY") }}
                          <span v-if="dueBadge" class="badge ml-1" :class="dueBadge.class">{{ dueBadge.label }}</span>
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Activities Card -->
            <div class="card table-card shadow mb-4" v-if="activities && activities.data">
              <div class="d-flex align-items-center px-4 pt-4 pb-3">
                <i class="fe fe-activity text-primary mr-2"></i>
                <h6 class="mb-0 font-weight-bold">Activities</h6>
              </div>
              <div class="card-body pt-0">
                <div v-if="activities.data.length">
                  <div v-for="activity in activities.data" :key="activity.id" class="media mb-3">
                    <div class="employee-avatar mr-3" style="width:36px;height:36px;flex-shrink:0;">
                      {{ activity.causer && activity.causer.full_name ? activity.causer.full_name.charAt(0) : '?' }}
                    </div>
                    <div class="media-body">
                      <p class="mb-0 font-weight-bold" style="font-size:.85rem;">{{ activity.causer && activity.causer.full_name }}</p>
                      <span class="badge badge-soft-info">{{ activity.description }}</span>
                      <small class="text-muted d-block mt-1">{{ activity.created_at | moment("from", "now") }}</small>
                    </div>
                  </div>
                </div>
                <p v-else class="text-muted text-center py-3" style="font-size:.85rem;">
                  <i class="fe fe-inbox mr-1"></i> No activities recorded
                </p>
              </div>
            </div>

          </div>
        </div>

        <!-- Webcam Modal -->
        <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content">
              <div class="container-fluid">
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
                <div class="row">
                  <div class="col-md-12 p-t-20 p-b-20">
                    <video id="video" width="100%" height="auto" autoplay></video>
                    <p>
                      <button @click="takePic" class="bg-navy btn btn-flat">Snap Photo</button>
                      <button type="button" class="btn btn-info" @click="savePic">Save pic</button>
                    </p>
                    <canvas id="canvas" width="720px" height="550px"></canvas>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </div>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
// Import component
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";
// Init plugin
// import { WebCam } from "vue-web-cam";
import paystack from "vue-paystack";
import { randomAlphaNumeric } from "@/utils/secureRandom";
import moment from "moment";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: {
    Navbar,
    Loading,
    paystack,
  },
  data() {
    return {
      user: null,
      agency,
      dependent: "",
      notes: "",
      medications: "",
      image: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
      imagefile: "",
      activities: "",
      pictureShower: true,
      renewalAmount: 9000,
      paystackkey: "pk_live_873084ab876aad40fe062000ee5a706de1b16f30", //paystack public key
    };
  },
  computed: {
    dueBadge() {
      if (!this.dependent || !this.dependent.expiry_date) {
        return null;
      }

      const daysLeft = moment(this.dependent.expiry_date).startOf("day").diff(moment().startOf("day"), "days");
      if (daysLeft < 0) {
        return { label: "Overdue", class: "badge-soft-danger" };
      }
      if (daysLeft <= 30) {
        return { label: "Due soon", class: "badge-soft-warning" };
      }
      return { label: "Valid", class: "badge-soft-success" };
    },
    needsRenewal() {
      // Available whether active or inactive — many renew ahead of the
      // previous expiry rather than waiting for it to lapse.
      return !!this.dependent
        && this.dependent.relationShipType === "Extra Dependent"
        && String(agency.id) === "313923";
    },
    renewalReference() {
      return randomAlphaNumeric(20);
    },
  },
  mounted() {
    //
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/getSpeicifDependant/${this.$route.params.id}`)
      .then((response) => {
        this.dependent = response.data.data;
        console.log(response);
      })
      .catch((error) => {
        console.error(error);
      });
  },

  methods: {
    onRenewalPaymentSuccess() {
      this.isLoading = true;
      const newExpiry = moment().add(12, "months").format("YYYY-MM-DD");
      this.axios
        .post("/api/v1/make/transaction", {
          agency_id: agency.id,
          amount: this.renewalAmount,
          description: "Extra Dependent Renewal Payment",
          type: "online",
          status: "created",
          transaction_ref: this.renewalReference,
          user_id: this.dependent.id,
        })
        .catch((e) => console.error(e))
        .finally(() => {
          Promise.all([
            this.axios.patch(`/api/v1/auth/extend-expiry/${this.dependent.id}`, { expiry_date: newExpiry }),
            this.axios.post(`/api/v1/auth/enableUser/${this.dependent.id}`),
          ])
            .then(() => {
              this.$toasted.success(`Payment received. Coverage extended to ${moment(newExpiry).format("MMM Do YYYY")}.`, {
                position: "top-center",
                duration: 5000,
              });
              this.fetchUser();
            })
            .catch((error) => {
              console.error(error);
              this.$toasted.error("Payment was logged, but updating the dependent failed. Please update manually.", {
                position: "top-center",
                duration: 5000,
              });
            })
            .finally(() => { this.isLoading = false; });
        });
    },
    onRenewalPaymentClose() {
      console.log("Renewal payment closed");
    },
    attachPic(event) {
      this.user = JSON.parse(localStorage.getItem("user"));

      console.log(event);
      // let files = event.target.files
      // let filename = event.target.files[0].name
      this.image = event.target.files[0];
      this.uploadPic();
    },
    uploadPic() {
      this.isLoading = true;
      this.user = JSON.parse(localStorage.getItem("user"));

      var formData = new FormData();
      formData.append("user_image", this.image);
      formData.append("dependant_id", this.$route.params.id);
      this.axios
        .post(
          "/api/v1/auth/uploadDependantImage",
          // {user_image: this.image, dependant_id: this.$route.params.id}
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$breadstick.notify("Profile Image changed Successfully!", {
            position: "top-right",
          });
          this.fetchUser();
        });
    },
    takePic() {
      var video = document.getElementById("video");
      var canvas = document.getElementById("canvas");
      var context = canvas.getContext("2d");

      context.drawImage(video, 0, 0, 640, 480);

      // get image
      var image = new Image();
      image.src = canvas.toDataURL("image/png");
      console.log(image);
      localStorage.setItem("snap", this.imagefile.src);
      this.imagefile = image.src;

      // upload image

      // end of upload image
    },

    savePic() {
      this.axios
        .post(`/api/v1/auth/uploadcustomerpicImage`, {
          user_image: this.imagefile,
          user_id: this.$route.params.id,
        })
        .then((response) => {
          console.log(response);
          this.$breadstick.notify("Profile Image changed Successfully!", {
            position: "top-right",
          });
          this.fetchUser();
        })
        .catch((error) => {
          console.error(error);
        });
    },

    fetchUser() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/getSpeicifDependant/${this.$route.params.id}`)
        .then((response) => {
          this.dependent = response.data.data;
           this.activities = response.data.activities;

          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    uploadPicSnapped() {
      this.isLoading = true;
      let snap = localStorage.getItem("snap");

      var formData = new FormData();
      formData.append("user_image", snap);
      this.axios
        .post("/api/v1/auth/uploadUserImage", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$breadstick.notify("Profile Image changed Successfully!", {
            position: "top-right",
          });
          // this.getUser()
        });
    },
  },
  created() {
    this.fetchUser();
  },
};
</script>
